import { api } from './client'
import type { 
  User, 
  Book, 
  Chapter, 
  ReadingRecord, 
  PaymentRecord, 
  Bookmark,
  CreatorEarningsByBook,
  CreatorEarningsSummary,
  PaginatedResponse 
} from '@/types/user'
import type {
  MoonlitBook,
  MoonlitBookListResponse,
  MoonlitChapterEntry,
  MoonlitGenreMeta
} from '@/types/moonlit'

// 认证相关API
export const authApi = {
  // 发送验证码
  sendCode: (phone: string) => 
    api.post('/auth/send-code', { phone }),
  
  // 用户注册
  register: (data: { phone: string; password: string; code: string }) =>
    api.post<{ user: User; token: string }>('/auth/register', data),
  
  // 用户登录
  login: (data: { phone: string; password: string }) =>
    api.post<{ user: User; token: string }>('/auth/login', data),
  
  // 获取用户信息
  getProfile: () =>
    api.get<{ user: User }>('/auth/profile'),
  
  // 刷新token
  refreshToken: () =>
    api.post<{ token: string }>('/auth/refresh')
}

// 书籍相关API
export const bookApi = {
  // 获取书籍列表
  getBooks: (params?: {
    page?: number
    limit?: number
    category?: 'male' | 'female' | 'general' | 'cyberpunk'
    brand?: 'pinelon' | 'moonlit'
    language?: string
    market?: string
    status?: 'draft' | 'published' | 'completed' | 'archived'
    search?: string
    subcategory?: string
  }) => api.get<PaginatedResponse<Book>>('/books', params),
  
  // 获取书籍详情
  getBook: (id: number) =>
    api.get<{ book: Book & { _count: { readingRecords: number; bookmarks: number } } }>(`/books/${id}`),
  
  // 获取书籍章节列表
  getBookChapters: (bookId: number, params?: { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Chapter>>(`/books/${bookId}/chapters`, params),
  
  // 按分类获取书籍
  getBooksByCategory: (
    category: 'male' | 'female' | 'general' | 'cyberpunk',
    params?: { page?: number; limit?: number; brand?: 'pinelon' | 'moonlit'; language?: string; market?: string }
  ) =>
    api.get<PaginatedResponse<Book>>(`/books/categories/${category}/books`, params),
  
  // 搜索书籍
  searchBooks: (
    query: string,
    params?: { page?: number; limit?: number; brand?: 'pinelon' | 'moonlit'; language?: string; market?: string }
  ) =>
    api.get<PaginatedResponse<Book>>('/books/search/books', { q: query, ...params }),

  // 创建新章节（AI续写后使用）
  createChapter: (bookId: number, data: { chapterNumber: number; title: string; content: string }) =>
    api.post<{ chapter: Chapter }>(`/books/${bookId}/chapters/create`, data),

  // 创建/续写用户私人分支，不改动公共书库母本
  forkChapter: (bookId: number, data: { chapterNumber: number; title: string; content: string; choiceType?: 'ai' | 'custom'; choiceText?: string; forkTitle?: string; forceNew?: boolean }) =>
    api.post<{ book: Book; chapter: Chapter; forked: boolean }>(`/books/${bookId}/fork-chapter`, data),

  // 将公共书另存为一条新的私人阅读
  forkReading: (bookId: number, data: { forkTitle: string; currentChapter?: number }) =>
    api.post<{ book: Book }>(`/books/${bookId}/fork-reading`, data),

  // 将私人分支发布为新的公共书
  publishPrivateBook: (bookId: number, data: { title: string; coverUrl: string; description?: string }) =>
    api.post<{ book: Book }>(`/books/${bookId}/publish`, data),

  // 从空白开头创建自由原创书
  createOriginalBook: (data: { title: string; coverUrl: string; opening: string; description?: string; category?: 'male' | 'female' | 'general' | 'cyberpunk' }) =>
    api.post<{ book: Book; chapter: Chapter }>('/books/original', data)
}

// 阅读相关API
export const readingApi = {
  // 获取章节内容
  getChapter: (chapterId: number) =>
    api.get<{ chapter: Chapter & { hasAccess: boolean } }>(`/reading/chapters/${chapterId}`),
  
  // 开始阅读
  startReading: (data: { bookId: number; chapterId: number }) =>
    api.post('/reading/start', data),
  
  // 更新阅读进度
  updateProgress: (data: { bookId: number; chapterId: number; progress: number }) =>
    api.put('/reading/progress', data),
  
  // 获取阅读历史
  getReadingHistory: (params?: { page?: number; limit?: number; brand?: 'pinelon' | 'moonlit'; language?: string; market?: string }) =>
    api.get<PaginatedResponse<ReadingRecord>>('/reading/history', params)
}

// Moonlit 英文内容 API
export const moonlitApi = {
  getGenres: () =>
    api.get<{ genres: MoonlitGenreMeta[] }>('/moonlit/genres'),

  getBooks: (params?: { page?: number; limit?: number; genre?: string; spice?: number }) =>
    api.get<MoonlitBookListResponse>('/moonlit/books', params),

  getBook: (slug: string) =>
    api.get<{ book: MoonlitBook }>(`/moonlit/books/${slug}`),

  getChapter: (slug: string, chapterNumber: number) =>
    api.get<MoonlitChapterEntry>(`/moonlit/books/${slug}/chapters/${chapterNumber}`),

  submitFeedback: (data: {
    storySlug?: string
    genre?: string
    chapterLength?: string
    downloadIntent?: string
    note?: string
    email?: string
  }) => api.post<{ feedbackId: number }>('/moonlit/feedback', data)
}

// AI相关API
export const aiApi = {
  // 获取AI服务状态
  getStatus: () =>
    api.get<{ deepseek: { available: boolean; error?: string }; coverImage: { available: boolean; error?: string } }>('/ai/status'),
  
  // 生成章节续写
  generateChapter: (data: { previousChapter: string; genre: string; targetLength: number; chapterTitle?: string; style?: string }) =>
    api.post('/ai/chapter/generate', data),
  
  // 批量生成章节选项（旧，保留兼容）
  generateChapterOptions: (data: { previousChapter: string; genre: string; targetLength: number; chapterTitle?: string; style?: string; numOptions?: number }) =>
    api.post<{ options: Array<{ content: string; suggestions?: string[] }>; count: number }>('/ai/chapter/options', data),

  // 生成剧情分支方向（短句摘要）
  generateBranchDirections: (data: { previousChapter: string; genre: string; numOptions?: number }) =>
    api.post<{ branches: Array<{ title: string; summary: string }>; count: number }>('/ai/chapter/branches', data),

  // 生成书名候选
  generateBookTitleOptions: (data: { originalTitle: string; description: string; genre: string; numOptions?: number }) =>
    api.post<{ titles: string[]; count: number }>('/ai/book/title-options', data),

  // 生成封面候选
  generateCoverOptions: (data: { novelTitle: string; novelDescription: string; genre: string; numOptions?: number }) =>
    api.post<{ options: Array<{ imageUrl: string; localPath: string; imageId: string; prompt: string; model: string }>; count: number }>('/ai/cover/options', data),
  
  // 健康检查
  healthCheck: () =>
    api.get('/ai/health')
}

// 剧情相关API
export const plotApi = {
  // 创建剧情树
  createPlotTree: (data: { bookId: string; initialContent: string; genre?: string }) =>
    api.post('/plot/tree/create', data),
  
  // 生成剧情推进
  generatePlot: (data: { treeId: string; branchCount?: number; targetWordCount?: number }) =>
    api.post('/plot/generate', data),
  
  // 选择分支
  chooseBranch: (data: { treeId: string; branchId: string }) =>
    api.post('/plot/branch/choose', data),
  
  // 获取剧情树信息
  getPlotTree: (treeId: string) =>
    api.get(`/plot/tree/${treeId}`),
  
  // 获取可用分支
  getAvailableBranches: (treeId: string) =>
    api.get(`/plot/tree/${treeId}/branches`),
  
  // 健康检查
  healthCheck: () =>
    api.get('/plot/health')
}

// 支付相关API
export const paymentApi = {
  // 模拟充值蜜豆
  rechargeMock: (data: { amountYuan: number; paymentMethod: 'wechat' | 'alipay' }) =>
    api.post<{ honeyBeans: number; balance: number; paymentMethod: string; exchangeRate: number }>('/payment/recharge/mock', data),

  // 使用蜜豆解锁三章付费包
  unlockChapterPack: (data: { chapterId: number }) =>
    api.post<{ paymentRecord: PaymentRecord; balance: number; pack: { start: number; end: number; price: number }; unlockedChapterNumbers: number[] }>('/payment/unlock-chapter-pack', data),

  // 创建支付订单
  createOrder: (data: { chapterId: number; amount: number; paymentMethod: string }) =>
    api.post<{ paymentRecord: PaymentRecord }>('/payment/create-order', data),
  
  // 模拟支付完成（仅开发/测试环境）
  simulatePayment: (data: { paymentId: number }) =>
    api.post<{ paymentRecord: PaymentRecord }>('/payment/simulate', data),
  
  // 获取支付记录
  getPaymentRecords: (params?: { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<PaymentRecord>>('/payment/records', params),
  
  // 获取支持的支付方式
  getPaymentMethods: () =>
    api.get<{ paymentMethods: Array<{ id: string; name: string; description: string; enabled: boolean }> }>('/payment/methods')
}

// 用户相关API
export const userApi = {
  // 获取我的书架
  getBookshelf: (params?: { page?: number; limit?: number; brand?: 'pinelon' | 'moonlit'; language?: string; market?: string }) =>
    api.get<PaginatedResponse<Book>>('/user/bookshelf', params),

  // 获取我的创作/上架作品
  getCreations: (params?: { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Book>>('/user/creations', params),

  // 获取创作者收益汇总
  getCreatorEarnings: () =>
    api.get<{ summary: CreatorEarningsSummary; items: CreatorEarningsByBook[] }>('/user/creator-earnings'),

  // 提交创作审核申诉
  appealCreation: (bookId: number, data: { reason: string }) =>
    api.post<{ book: Book }>(`/user/creations/${bookId}/appeal`, data),
  
  // 加入书架
  addToBookshelf: (bookId: number) =>
    api.post(`/user/bookshelf/${bookId}`),
  
  // 移出书架
  removeFromBookshelf: (bookId: number) =>
    api.delete(`/user/bookshelf/${bookId}`),
  
  // 更新用户信息
  updateProfile: (data: Partial<{ nickname: string; avatarUrl: string }>) =>
    api.put('/user/profile', data),
  
  // 获取账户余额
  getBalance: () =>
    api.get<{ balance: number }>('/user/balance')
}

// 导出所有API
export default {
  auth: authApi,
  book: bookApi,
  reading: readingApi,
  ai: aiApi,
  plot: plotApi,
  payment: paymentApi,
  moonlit: moonlitApi,
  user: userApi
}
